import type { NodeContextPayload } from './node-context'

export interface NodeOpRefsData {
  op?: unknown
  op_data?: Record<string, unknown>
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function readCurrentOpKey(nodeData: NodeOpRefsData) {
  return typeof nodeData.op === 'string' && nodeData.op.trim().length > 0
    ? nodeData.op
    : ''
}

function readStoredCurrentOpData(nodeData: NodeOpRefsData) {
  const op = readCurrentOpKey(nodeData)
  if (!op || !isRecord(nodeData.op_data)) {
    return undefined
  }

  const opData = nodeData.op_data[op]
  return isRecord(opData) ? opData : undefined
}

function ensureCurrentOpData(nodeData: NodeOpRefsData) {
  const op = readCurrentOpKey(nodeData)
  if (!op) {
    return undefined
  }

  const opDataRoot = nodeData.op_data ??= {}
  const current = opDataRoot[op]
  const opData = isRecord(current) ? current : {}
  opDataRoot[op] = opData
  return opData
}

/**
 * 读取上下文条目的排序引用 ID。
 *
 * slot reference 会把同一条 edge 展开成多个上下文 payload，真实 edgeId 不能再唯一代表一张参考图；
 * UI 排序、渲染 key、op_data.refs 都使用 contextId 优先，真实删线/改线仍继续使用 edgeId。
 */
export function readContextPayloadRefId(
  item: Pick<NodeContextPayload, 'contextId' | 'edgeId'>,
) {
  return item.contextId ?? item.edgeId
}

export function orderContextPayloadsByRefs<T extends Pick<NodeContextPayload, 'contextId' | 'edgeId'>>(
  items: T[],
  refs?: readonly string[],
) {
  if (!refs?.length) {
    return items
  }

  const itemsByRefId = new Map<string, T[]>()
  items.forEach((item) => {
    // refs 已统一存上下文排序 ID；普通边没有 contextId 时自然退回 edgeId。
    const refId = readContextPayloadRefId(item)
    const refGroup = itemsByRefId.get(refId) ?? []
    refGroup.push(item)
    itemsByRefId.set(refId, refGroup)
  })

  const orderedItems: T[] = []
  const orderedRefIds = new Set<string>()
  refs.forEach((refId) => {
    itemsByRefId.get(refId)?.forEach((item) => {
      const itemRefId = readContextPayloadRefId(item)
      if (orderedRefIds.has(itemRefId)) return

      orderedItems.push(item)
      orderedRefIds.add(itemRefId)
    })
  })
  const unorderedItems = items.filter((item) => !orderedRefIds.has(readContextPayloadRefId(item)))

  // 新增但还没进入 refs 的入边放到末尾，兼容旧数据和外部连线。
  return [...orderedItems, ...unorderedItems]
}

export function readCurrentOpRefs(nodeData: NodeOpRefsData) {
  const refs = readStoredCurrentOpData(nodeData)?.refs
  return Array.isArray(refs)
    ? refs.filter((ref): ref is string => typeof ref === 'string')
    : []
}

/**
 * 按当前上下文 payload 同步当前 OP 的 refs。
 *
 * slot reference 创建边时可能先写入父 edgeId；等 payload 展开后，这里只保留每个 slot 的 contextId。
 * 同时会清理已不存在的同 edge slot ref，避免删除 slot 或空 slot 后遗留脏引用。
 */
export function syncCurrentOpRefsByPayloads(
  nodeData: NodeOpRefsData,
  items: readonly Pick<NodeContextPayload, 'contextId' | 'edgeId'>[],
) {
  const opData = ensureCurrentOpData(nodeData)
  if (!opData) return

  const refs = readCurrentOpRefs(nodeData)
  const itemRefIds = items.map((item) => readContextPayloadRefId(item))
  const itemRefIdSet = new Set(itemRefIds)

  // 过滤掉所有在当前 items 中已经不存在的 ref (比如用户主动删除了该 edge 或 slot)
  const nextRefs = refs.filter((refId) => itemRefIdSet.has(refId))

  // 将新增的 ref 追加到末尾
  itemRefIds.forEach((refId) => {
    if (!nextRefs.includes(refId)) {
      nextRefs.push(refId)
    }
  })

  if (nextRefs.length !== refs.length || nextRefs.some((refId, index) => refId !== refs[index])) {
    opData.refs = nextRefs
  }
}

export function reorderCurrentOpRefs(
  nodeData: NodeOpRefsData,
  fromRefId: string,
  toRefId: string,
) {
  const refs = readCurrentOpRefs(nodeData)
  if (!refs || refs.length < 2) return

  const fromIdx = refs.indexOf(fromRefId)
  const toIdx = refs.indexOf(toRefId)
  if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) return

  const tmp = refs[fromIdx]
  refs[fromIdx] = refs[toIdx]
  refs[toIdx] = tmp

  const opData = ensureCurrentOpData(nodeData)
  if (opData) {
    opData.refs = refs
  }
}

function isEdgeOrSlotContextRef(value: unknown, edgeId: string) {
  // slot reference 的排序 ID 由 edgeId 派生，删除整条边时要同时移除这些派生引用。
  return typeof value === 'string' && (value === edgeId || value.startsWith(`${edgeId}:slot:`))
}

function removeEdgeIdFromOpData(opData: Record<string, unknown>, edgeId: string) {
  Object.entries(opData).forEach(([key, value]) => {
    if (isEdgeOrSlotContextRef(value, edgeId)) {
      delete opData[key]
      return
    }

    if (Array.isArray(value)) {
      const nextValue = value.filter((item) => !isEdgeOrSlotContextRef(item, edgeId))
      if (nextValue.length === value.length) return

      if (nextValue.length === 0) {
        delete opData[key]
        return
      }

      opData[key] = nextValue
    }
  })
}

export function removeEdgeIdFromAllOpRefs(
  nodeData: NodeOpRefsData,
  edgeId: string,
) {
  if (!isRecord(nodeData.op_data)) return

  Object.entries(nodeData.op_data).forEach(([op, opData]) => {
    if (!isRecord(opData)) return

    removeEdgeIdFromOpData(opData, edgeId)
    if (Object.keys(opData).length === 0) {
      delete nodeData.op_data?.[op]
    }
  })

  if (Object.keys(nodeData.op_data).length === 0) {
    delete nodeData.op_data
  }
}
